import { FC, useMemo } from "react";
import noop from "lodash/noop";

import { InlineMapper } from "./inline-mapper";
import { ArrayMapping, InlineMapperProps } from "../types";
import { ColumnOption, useFormkitContext } from "../formkit-context";

type ArrayInlineMapperProps = Omit<InlineMapperProps, "value" | "type"> & {
  value: ArrayMapping;
  onReloadEligibleInlineMapperColumns: (currentSelectedColumn?: string) => void;
};

function findOptionByValue(
  groups: ColumnOption[] | undefined,
  value: string | undefined,
): ColumnOption | undefined {
  // Event-schema suggestions come in as grouped `ColumnOption[]` and array fields carry
  // nested `elementOptions` (e.g. selecting `properties.users` should surface `id`, `active`, etc).
  // This helper finds the *selected* array field option by its `value` so we can scope the
  // inline mapper to that array element's properties.
  if (!groups || !value) return undefined;
  for (const group of groups) {
    for (const opt of group.options ?? []) {
      if (opt.value === value) return opt;
    }
  }
  return undefined;
}

export const ArrayInlineMapper: FC<Readonly<ArrayInlineMapperProps>> = (
  props,
) => {
  const {
    jsonColumnProperties,
    onChangeJsonColumnProperties,
    value,
    enabledNestedInlineMapper,
    onReloadEligibleInlineMapperColumns,
    columnOptionsOverride,
  } = props;
  const { model, columns: contextColumns } = useFormkitContext();
  const isEventModel = Boolean(model?.isEventModel);
  const scopedColumns = columnOptionsOverride ?? contextColumns;

  const onReloadJsonColumnOptions = (currentSelectedColumn?: string) => {
    if (jsonColumnProperties.allColumnsProps) {
      onChangeJsonColumnProperties((currentState) => ({
        ...currentState,
        selectedColumnProps: value.from
          ? currentState.allColumnsProps?.[value.from]
          : undefined,
      }));
    } else {
      onReloadEligibleInlineMapperColumns(currentSelectedColumn);
    }
  };

  const elementColumnsOverride: ColumnOption[] | undefined = useMemo(() => {
    if (!isEventModel) return undefined;
    const selected = findOptionByValue(scopedColumns, value.from);
    const elementOptions = selected?.elementOptions ?? [];
    return [
      {
        label: "Array element properties",
        value: "",
        type: null,
        options: elementOptions,
      },
    ];
  }, [isEventModel, scopedColumns, value.from]);

  return (
    <InlineMapper
      {...props}
      enabledNestedInlineMapper={enabledNestedInlineMapper}
      columnOptionsOverride={elementColumnsOverride}
      overwriteColumnsWithArrayProps={!isEventModel}
      type="array"
      onReloadEligibleInlineMapperColumns={
        isEventModel ? noop : onReloadJsonColumnOptions
      }
    />
  );
};
